/**
 * The prisindikation button on a product page (ELD-149)
 * ------------------------------------------------------
 * One control, two behaviours, and who the visitor is decides which.
 *
 * NOBODY WITH AN ÄRENDE: it is a link to the landing page carrying this
 * product, which is what ?produkt= is for. They have not asked for a price on
 * anything yet, so they should meet the offer, and the landing page shows this
 * very stove at the top when it is named.
 *
 * SOMEBODY WHO ALREADY HAS ONE: taking them to the landing page would be the
 * site explaining an offer they already accepted. So the stove is added and
 * they stay where they are, with a toast that says so and points at their
 * ärende. That is the difference between a visitor and a customer, and the
 * page should know which it is talking to.
 *
 * HOW IT KNOWS, cheapest first:
 *   1. The flow's own localStorage. Same origin, so the token of a sent
 *      ärende and the answers of one in progress are both readable here, with
 *      no request at all. Covers this browser, which is most of the time.
 *   2. The picking flag, ?view=prisindikator, kept for the tab. Somebody who
 *      came from Din sida to find more stoves.
 *   3. /a/min-sida/status, and ONLY when Liquid says a customer is signed in.
 *      Covers the case the first two cannot: sent from another device, signed
 *      in here. One small request, on a page a signed-in customer opened, and
 *      never for a signed-out visitor.
 *
 * Saying so is a band at the top of the page that pushes the page down, not a
 * card in a corner: the corner belongs to the chat widget, and a message down
 * there reads as the page muttering rather than answering.
 *
 * Adding writes through EldstadFavourites, the same store the heart uses and
 * the same one the flow reads (ELD-111). There is no second list, and the
 * stove joins the ärende the next time Din sida is opened.
 */
(function () {
  'use strict';

  var PARAM = 'view';
  var FLAG = 'prisindikator';
  var PICKING_KEY = 'eldstad_prisindikation_picking';

  /* The flow's own keys and addresses, from app/prisindikation/keep.ts and
     app/prisindikation/flow.ts. Two languages, one contract: change either
     side and change this. */
  var ANSWERS_KEY = 'eldstad.prisindikation.answers';
  var TOKEN_KEY = 'eldstad.prisindikation.nyckel';
  var ARENDE = '/a/min-sida/arende';
  var STATUS = '/a/min-sida/status';

  /** How long the band stays. Long enough to read twice, short enough that it
      is gone before it is in the way. */
  var TOAST_MS = 6000;

  /* --------------------------------------------------------------- who */

  /**
   * Did they come here from Din sida to pick more stoves?
   *
   * Din sida sends them to the catalogue with ?view=prisindikator. That flag
   * has to survive the walk from the catalogue to a product and on to the
   * next product, so the first page that sees it writes it to sessionStorage
   * and every page after reads it from there. Session, not local: it is true
   * of this errand, not of this browser forever.
   */
  function picking() {
    var flagged = false;
    try {
      flagged = new URLSearchParams(window.location.search).get(PARAM) === FLAG;
    } catch (e) {
      /* No URLSearchParams, or a malformed query. Fall through to storage. */
    }
    try {
      if (flagged) {
        window.sessionStorage.setItem(PICKING_KEY, '1');
        return true;
      }
      return window.sessionStorage.getItem(PICKING_KEY) === '1';
    } catch (e) {
      /* Storage refused (private mode, blocked cookies). The URL still counts. */
      return flagged;
    }
  }

  /**
   * Does this browser already hold an ärende, or a flow in progress?
   *
   * Both keys belong to the app behind the proxy, and the proxy is on this
   * same origin, so they are readable here with no request at all. A token
   * means an ärende was sent from this browser; answers mean one is being
   * filled in. Either way the offer has been accepted and the page should add
   * rather than explain.
   */
  function knownHere() {
    try {
      var store = window.localStorage;
      if (!store) return false;
      if (store.getItem(TOKEN_KEY)) return true;
      var answers = store.getItem(ANSWERS_KEY);
      return Boolean(answers && answers !== '{}' && answers !== 'null');
    } catch (e) {
      return false;
    }
  }

  /* -------------------------------------------------------------- banner */

  /**
   * A band at the top of the WINDOW that grows downwards and pushes the page
   * down, rather than a card floating in a corner (Jonas, 2026-09-24).
   * A corner toast on this site competes with the chat widget and reads as
   * something the page is muttering; a band at the top reads as the site
   * answering.
   *
   * Fixed, not in the flow. In the flow it was only visible from the top of
   * the page, so pressing the button halfway down a product page confirmed
   * the add to nobody. It belongs with the window chrome.
   *
   * The push is --eldstad-pi-band, set here to the band's measured height and
   * back to zero on the way out. The page takes it as padding at the top and
   * the sticky header as its own offset, so nothing sits underneath the band
   * at any scroll position.
   *
   * The height animates through grid-template-rows 0fr to 1fr, which is the
   * one way to transition to a height nobody has measured. A max-height with a
   * guessed ceiling gets the easing wrong the moment a line wraps.
   *
   * It goes AFTER the skip link, never before it: the band holds a link and a
   * button, and "hoppa till innehåll" has to stay the first thing Tab finds.
   */
  var showing = null;

  /** How far the page is held down. Zero is no band. */
  function push(px) {
    document.documentElement.style.setProperty('--eldstad-pi-band', px + 'px');
  }

  function dismiss(immediately) {
    if (!showing) return;
    var el = showing.el;
    window.clearTimeout(showing.timer);
    showing = null;
    push(0);
    if (immediately) {
      el.remove();
      return;
    }
    el.classList.remove('is-in');
    el.addEventListener('transitionend', function () { el.remove(); }, { once: true });
    // A belt for the case where no transition fires (reduced motion).
    window.setTimeout(function () { if (el.parentNode) el.remove(); }, 400);
  }

  function toast(message, linkText) {
    dismiss(true);

    var el = document.createElement('div');
    el.className = 'eldstad-pi-banner';
    /* status, not alert: this reports something the reader just did. An alert
       interrupts a screen reader mid-sentence, and nothing here is urgent. */
    el.setAttribute('role', 'status');

    var clip = document.createElement('div');
    clip.className = 'eldstad-pi-banner__clip';
    el.appendChild(clip);

    var row = document.createElement('div');
    row.className = 'eldstad-pi-banner__row';
    clip.appendChild(row);

    var text = document.createElement('span');
    text.className = 'eldstad-pi-banner__text';
    text.textContent = message;
    row.appendChild(text);

    var link = document.createElement('a');
    link.className = 'eldstad-pi-banner__link';
    link.href = ARENDE;
    link.textContent = linkText;
    row.appendChild(link);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'eldstad-pi-banner__close';
    close.setAttribute('aria-label', 'Stäng');
    close.textContent = '\u00d7';
    close.addEventListener('click', function () { dismiss(false); });
    row.appendChild(close);

    var skip = document.querySelector('.skip-to-content-link');
    if (skip && skip.parentNode === document.body) {
      document.body.insertBefore(el, skip.nextSibling);
    } else {
      document.body.insertBefore(el, document.body.firstChild);
    }

    /* A frame later, so the grid row has a closed state to open from. The row
       is measured rather than guessed: it is the clip that collapses, not the
       row, so the row already has its full height here and a second line of
       text is accounted for. */
    window.requestAnimationFrame(function () {
      el.classList.add('is-in');
      push(row.offsetHeight);
    });

    showing = {
      el: el,
      timer: window.setTimeout(function () { dismiss(false); }, TOAST_MS),
    };
  }

  /* ---------------------------------------------------------------- wire */

  function add(button) {
    var store = window.EldstadFavourites;
    var handle = button.dataset.handle;
    if (!store || typeof store.add !== 'function' || !handle) return false;
    store.add(handle, { category: button.dataset.category || undefined });
    return true;
  }

  function becomeAdder(link) {
    /* A button, not a link, because it no longer goes anywhere. Same classes,
       so it keeps the theme's own box. */
    var button = document.createElement('button');
    button.type = 'button';
    button.className = link.className;
    button.dataset.prisindikationButton = '';
    button.dataset.handle = link.dataset.handle || '';
    if (link.dataset.category) button.dataset.category = link.dataset.category;
    button.textContent = 'Räkna på den här';
    button.hidden = false;
    link.parentNode.replaceChild(button, link);

    var store = window.EldstadFavourites;
    if (store && typeof store.has === 'function' && store.has(button.dataset.handle)) {
      markAdded(button);
    }

    button.addEventListener('click', function () {
      if (!add(button)) return;
      markAdded(button);
      toast('Tillagd i din prisindikation.', 'Se ditt ärende');
    });
    return button;
  }

  /* "Räkna på den här" and "Räknas med", because Din sida already says
     "Räkna inte på den här" beside a stove that is on the ärende. One
     vocabulary across the two surfaces, and both fit a phone. */
  function markAdded(button) {
    button.disabled = true;
    button.classList.add('is-added');
    button.textContent = 'Räknas med';
  }

  function wire(root) {
    var links = (root || document).querySelectorAll('a[data-prisindikation-button]');
    if (links.length === 0) return;

    var here = picking() || knownHere();
    Array.prototype.forEach.call(links, function (link) {
      if (!link.dataset.handle) return;
      if (here) {
        becomeAdder(link);
        return;
      }
      // The ordinary offer. Shown as the link it already is.
      link.hidden = false;

      /* Last resort, and only for a signed-in customer: they may have sent
         from another device. Asked after the link is already usable, so a slow
         answer never holds the page, and the link simply becomes an add if the
         answer is yes before they press it. */
      if (link.dataset.signedIn !== 'true') return;
      fetch(STATUS, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (body) {
          if (body && body.arende && link.parentNode) becomeAdder(link);
        })
        .catch(function () {
          /* No answer is the same as no ärende: the link still works. */
        });
    });
  }

  window.EldstadPrisindikation = { picking: picking, knownHere: knownHere, wire: wire, toast: toast, dismiss: dismiss };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { wire(document); });
  } else {
    wire(document);
  }
})();
